import { parseName } from 'humanparser'

/**
 * First and last name from a single display name, for email tools.
 *
 * The `users.first_name` column holds the whole sign-in display name
 * (`registerUser` copies Firebase's `displayName` into both `first_name` and
 * `full_name`), so a greeting read straight off it says "Hi Andrew Jimenez".
 * Customer.io's `first_name` / `last_name` must be split here instead.
 *
 * Parsed with `humanparser`, the same parser the site's `useUserData` uses, so
 * the site and Customer.io split a name the same way. It drops salutations
 * ("Dr."), keeps compound surnames whole ("van der Berg"), and takes the middle
 * name out of both halves ("Mary Ann Smith" → Mary / Smith).
 *
 * `lastName` deliberately EXCLUDES the suffix ("Jr.", "III"): it is the surname
 * a segment or a "Dear Ms Smith" line needs, and a suffix glued onto it would
 * not match the same person's surname from any other source.
 *
 * An email address typed as a name is not a name and yields nothing:
 * `parseName` would hand the whole address back as the first name.
 */
export interface SplitPersonName {
  firstName: string | null
  lastName: string | null
}

const clean = (value: string | undefined): string | null => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

export const splitPersonName = (
  name: string | null | undefined
): SplitPersonName => {
  // Whitespace collapsed first so tabs and double spaces never reach a part.
  const normalized =
    typeof name === 'string' ? name.trim().replace(/\s+/g, ' ') : ''
  if (!normalized || normalized.includes('@')) {
    return { firstName: null, lastName: null }
  }
  const parsed = parseName(normalized)
  return {
    firstName: clean(parsed.firstName),
    lastName: clean(parsed.lastName),
  }
}

/**
 * The same split as PostHog person properties (`firstName` / `lastName`), with
 * empty parts left out so a write never blanks a name set elsewhere. The
 * Customer.io destination greets with `person.properties.firstName`.
 */
export const personNameProps = (
  name: string | null | undefined
): { firstName?: string; lastName?: string } => {
  const { firstName, lastName } = splitPersonName(name)
  return {
    ...(firstName ? { firstName } : {}),
    ...(lastName ? { lastName } : {}),
  }
}
